import type { ComponentType } from 'react'
import { cn } from '@/lib/utils'

// One choice from a few, as a row of pills: the album page's layout switch, with words, and an icon beside each
// word when `icons` gives one (those pills are a little narrower so a row of four fits a phone).
export function Pills<T extends string>({
  label,
  value,
  options,
  onChange,
  compact,
  icons,
}: {
  label: string
  value: T
  options: [T, string][]
  onChange: (value: T) => void
  // Narrower pills, for a switch that shares its row (beside a search box on a phone).
  compact?: boolean
  icons?: Partial<Record<T, ComponentType<{ className?: string }>>>
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex max-w-full shrink-0 [scrollbar-width:none] gap-0.5 overflow-x-auto rounded-full bg-secondary p-0.5"
    >
      {options.map(([option, name]) => {
        const Icon: ComponentType<{ className?: string }> | undefined = icons?.[option]
        return (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={cn(
              'min-h-9 rounded-full text-sm',
              Icon && 'inline-flex items-center gap-1.5',
              compact || Icon ? 'px-3' : 'px-4',
              'font-medium text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:bg-brand aria-pressed:text-brand-foreground pointer-coarse:min-h-11',
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" />}
            {name}
          </button>
        )
      })}
    </div>
  )
}
